/**
 * Project Service
 * API calls for project management
 */

import apiClient from '../client';
import type { Project, PaginatedResponse } from '../../types';

interface ProjectFilters {
  page?: number;
  size?: number;
  status?: string;
}

const projectService = {
  /**
   * Get all projects with pagination
   */
  getProjects: async (filters: ProjectFilters = {}) => {
    const { page = 0, size = 20, status } = filters;
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    if (status) params.append('status', status);

    const response = await apiClient.get<PaginatedResponse<Project>>(
      `/projects?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get active projects
   */
  getActiveProjects: async (filters: Omit<ProjectFilters, 'status'> = {}) => {
    const { page = 0, size = 20 } = filters;
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const response = await apiClient.get<PaginatedResponse<Project>>(
      `/projects/active?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get project by ID
   */
  getProjectById: async (id: string) => {
    const response = await apiClient.get<Project>(`/projects/${id}`);
    return response.data;
  },

  /**
   * Create new project
   */
  createProject: async (data: Partial<Project>) => {
    const response = await apiClient.post<Project>('/projects', data);
    return response.data;
  },

  /**
   * Update project
   */
  updateProject: async (id: string, data: Partial<Project>) => {
    const response = await apiClient.put<Project>(`/projects/${id}`, data);
    return response.data;
  },

  closeProject: async (id: string) => {
    await apiClient.patch(`/projects/${id}/close`);
  },

  reopenProject: async (id: string) => {
    await apiClient.patch(`/projects/${id}/reopen`);
  },

  pauseProject: async (id: string) => {
    const response = await apiClient.patch<Project>(`/projects/${id}/pause`);
    return response.data;
  },

  resumeProject: async (id: string) => {
    const response = await apiClient.patch<Project>(`/projects/${id}/resume`);
    return response.data;
  },

  /**
   * Delete project
   */
  deleteProject: async (id: string) => {
    await apiClient.delete(`/projects/${id}`);
  },

  search: async (params: { page?: number; size?: number; search?: string; active?: boolean } = {}) => {
    const { page = 0, size = 10, search, active } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (active !== undefined) qs.set('active', String(active));
    const response = await apiClient.get<PaginatedResponse<Project>>(`/projects/search?${qs}`);
    return response.data;
  },
};

export default projectService;
